import React from 'react';
import Icon from '../../../components/AppIcon';

const ComplianceDashboard = ({ metrics }) => {
  const getStatusIcon = (type) => {
    switch (type) {
      case 'current':
        return 'CheckCircle';
      case 'expiring':
        return 'Clock';
      case 'expired':
        return 'AlertCircle';
      case 'total':
        return 'Users';
      default:
        return 'Circle';
    }
  };

  const getStatusColor = (type) => {
    switch (type) {
      case 'current':
        return 'text-success bg-success/10';
      case 'expiring':
        return 'text-warning bg-warning/10';
      case 'expired':
        return 'text-error bg-error/10';
      case 'total':
        return 'text-primary bg-primary/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const complianceCards = [
    {
      title: 'Total Employees',
      value: metrics?.totalEmployees,
      type: 'total',
      description: 'Active employees requiring training'
    },
    {
      title: 'Fully Compliant',
      value: metrics?.compliantEmployees,
      type: 'current',
      description: 'All certifications current'
    },
    {
      title: 'Expiring Soon',
      value: metrics?.expiringSoon,
      type: 'expiring',
      description: 'Certifications expire within 30 days'
    },
    {
      title: 'Non-Compliant',
      value: metrics?.nonCompliant,
      type: 'expired',
      description: 'Expired or missing certifications'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {complianceCards?.map((card, index) => (
          <div key={index} className="bg-card rounded-lg border border-border p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">{card?.title}</p>
                <p className="text-2xl font-bold text-foreground mt-2">{card?.value}</p>
                <p className="text-xs text-muted-foreground mt-1">{card?.description}</p>
              </div>
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${getStatusColor(card?.type)}`}>
                <Icon name={getStatusIcon(card?.type)} size={24} />
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Department Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card rounded-lg border border-border p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Department Compliance</h3>
          <div className="space-y-4">
            {metrics?.departmentBreakdown?.map((dept, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-foreground">{dept?.name}</span>
                    <span className="text-sm text-muted-foreground">{dept?.compliance}%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        dept?.compliance >= 80 ? 'bg-success' :
                        dept?.compliance >= 60 ? 'bg-warning' : 'bg-error'
                      }`}
                      style={{ width: `${dept?.compliance}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Training Trends</h3>
          <div className="space-y-4">
            {metrics?.trainingTrends?.map((trend, index) => (
              <div key={index} className="flex items-center space-x-3">
                <div className={`w-3 h-3 rounded-full ${
                  trend?.trend === 'up' ? 'bg-success' :
                  trend?.trend === 'down' ? 'bg-error' : 'bg-warning'
                }`} />
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">{trend?.certification}</p>
                  <p className="text-xs text-muted-foreground">
                    {trend?.completions} completions this month
                  </p>
                </div>
                <Icon 
                  name={trend?.trend === 'up' ? 'TrendingUp' : trend?.trend === 'down' ? 'TrendingDown' : 'Minus'} 
                  size={16} 
                  className={
                    trend?.trend === 'up' ? 'text-success' :
                    trend?.trend === 'down' ? 'text-error' : 'text-warning'
                  }
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Upcoming Renewals */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Upcoming Renewals</h3>
          <span className="text-sm text-muted-foreground">Next 30 days</span>
        </div>
        <div className="space-y-3">
          {metrics?.upcomingRenewals?.map((renewal, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                  <span className="text-xs font-medium text-primary-foreground">
                    {renewal?.employee?.split(' ')?.map(n => n?.[0])?.join('')}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">{renewal?.employee}</p>
                  <p className="text-xs text-muted-foreground">{renewal?.certification}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-foreground">{renewal?.daysLeft} days</p>
                <p className="text-xs text-muted-foreground">Expires {renewal?.expiryDate}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ComplianceDashboard;